import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface RequestForm {
  email: string;
}

interface ResetForm {
  code: string;
  newPassword: string;
  confirmPassword: string;
}

const ForgotPasswordPage: React.FC = () => {
  const { forgotPassword, confirmForgotPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');
  const [error, setError] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  const {
    register: registerRequest,
    handleSubmit: handleRequestSubmit,
    formState: { errors: requestErrors, isSubmitting: isRequesting },
  } = useForm<RequestForm>();

  const {
    register: registerReset,
    handleSubmit: handleResetSubmit,
    formState: { errors: resetErrors, isSubmitting: isResetting },
    watch,
  } = useForm<ResetForm>();

  const newPassword = watch('newPassword');

  const onRequest = async (data: RequestForm) => {
    setError(null);
    try {
      await forgotPassword(data.email);
      setPendingEmail(data.email);
    } catch (err: any) {
      setError(err.message || 'Failed to send reset code');
    }
  };

  const onReset = async (data: ResetForm) => {
    setError(null);
    if (!pendingEmail) return;
    try {
      await confirmForgotPassword(pendingEmail, data.code, data.newPassword);
      navigate(returnUrl ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : '/login', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Password reset failed');
    }
  };

  if (pendingEmail) {
    return (
      <div className="main-content">
        <div className="auth-page">
          <div className="form-container auth-form-container">
            <h2>Reset Password</h2>
            <p className="auth-subtitle">Enter the code sent to <strong>{pendingEmail}</strong></p>
            {error && <p className="auth-error">{error}</p>}
            <form onSubmit={handleResetSubmit(onReset)} className="admin-form">
              <div className="form-group">
                <label htmlFor="code">Verification Code</label>
                <input
                  id="code"
                  type="text"
                  className={resetErrors.code ? 'error' : ''}
                  {...registerReset('code', { required: 'Verification code is required' })}
                />
                {resetErrors.code && <span className="error-text">{resetErrors.code.message}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  id="newPassword"
                  type="password"
                  className={resetErrors.newPassword ? 'error' : ''}
                  {...registerReset('newPassword', {
                    required: 'New password is required',
                    minLength: { value: 8, message: 'Password must be at least 8 characters' },
                    validate: (value) => {
                      if (!/[A-Z]/.test(value)) return 'Must contain an uppercase letter';
                      if (!/[a-z]/.test(value)) return 'Must contain a lowercase letter';
                      if (!/[0-9]/.test(value)) return 'Must contain a number';
                      return true;
                    },
                  })}
                />
                {resetErrors.newPassword && <span className="error-text">{resetErrors.newPassword.message}</span>}
                <span className="form-help">8+ characters, uppercase, lowercase, and a number</span>
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  className={resetErrors.confirmPassword ? 'error' : ''}
                  {...registerReset('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) => value === newPassword || 'Passwords do not match',
                  })}
                />
                {resetErrors.confirmPassword && (
                  <span className="error-text">{resetErrors.confirmPassword.message}</span>
                )}
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={isResetting}>
                  {isResetting ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
            <div className="auth-links">
              <p><Link to={returnUrl ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : '/login'}>Back to login</Link></p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="auth-page">
        <div className="form-container auth-form-container">
          <h2>Forgot Password</h2>
          <p className="auth-subtitle">Enter your email to receive a password reset code.</p>
          {error && <p className="auth-error">{error}</p>}
          <form onSubmit={handleRequestSubmit(onRequest)} className="admin-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                className={requestErrors.email ? 'error' : ''}
                {...registerRequest('email', { required: 'Email is required' })}
              />
              {requestErrors.email && <span className="error-text">{requestErrors.email.message}</span>}
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={isRequesting}>
                {isRequesting ? 'Sending...' : 'Send Reset Code'}
              </button>
            </div>
          </form>
          <div className="auth-links">
            <p><Link to={returnUrl ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : '/login'}>Back to login</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
